/**
 * CommentOptions Component
 *
 * Renders the options menu for a forum comment, including edit, report, and delete actions.
 * Handles admin and user permissions, and supports subcomments.
 *
 * Features:
 * - Displays menu with edit, report, and delete options
 * - Handles actions for comments and subcomments
 * - Integrates with Redux for state management
 * - Uses Material-UI for UI elements
 * - Passes relevant props to child components for actions
 * - Uses CSS modules for scoped styling
 *
 * @param {Object} props - Component properties
 * @param {boolean} [props.adminComment] - Indicates if the comment is by an admin
 * @param {number} [props.idLesson] - Lesson ID associated with the comment
 * @param {number} [props.idCourse] - Course ID associated with the comment
 * @param {boolean} [props.subcomment] - Indicates if the comment is a subcomment
 * @param {number} [props.idComment] - ID of the comment or subcomment
 * @param {function} [props.showAnswers] - Function to refresh answers after deletion
 * @param {function} [props.editCommentHandler] - Function to trigger edit mode
 * @returns {JSX.Element} Rendered CommentOptions component
 */

import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import IconButton from '@mui/material/IconButton';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import {
  deleteComment,
  deleteSubcomment,
  getCommentsForum,
  reportComment,
  reportSubComment
} from 'store/lesson-actions';

import classes from './CommentOptions.module.css';

const CommentOptions = props => {
  const { t } = useTranslation(['forum']);
  const dispatch = useDispatch();
  const userInfo = useSelector(state => state.userInfo);
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  /**
   * Opens the options menu
   */
  const openMenuHandler = event => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the options menu
   */
  const CloseMenuHandler = () => {
    setAnchorEl(null);
  };

  /**
   * Handles reporting a comment or subcomment
   */
  const reportPostHandler = () => {
    (async () => {
      if (props.subcomment) {
        await reportSubComment(props.idComment, true);
      } else {
        await reportComment(props.idComment, true);
      }
    })();
    setAnchorEl(null);
  };

  /**
   * Handles deleting a comment or subcomment
   */
  const deleteCommentHandler = () => {
    (async () => {
      let comments = [];
      if (props.subcomment) {
        comments.push({ idSubComment: props.idComment });
        let deleteSubcommentStatus = await deleteSubcomment(
          comments,
          false
        );
        if (deleteSubcommentStatus) {
          props.showAnswers();
        }
      } else {
        comments.push({ idComment: props.idComment });
        let deleteCommentStatus = await deleteComment(
          comments,
          false
        );
        if (deleteCommentStatus) {
          dispatch(
            getCommentsForum(props.idLesson, 'es', 50, 1, true)
          );
        }
      }
    })();
    setAnchorEl(null);
  };

  /**
   * Handles editing a comment
   */
  const editPostHandler = () => {
    props.editCommentHandler();
    setAnchorEl(null);
  };

  return (
    <>
      {/* Material-UI IconButton for options menu */}
      <IconButton
        className={
          props.subcomment
            ? classes.moreOptionsSubcommentIcon
            : classes.moreOptionsIcon
        }
        onClick={openMenuHandler}
      >
        <MoreHorizIcon />
      </IconButton>
      {/* Material-UI Menu for options */}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={CloseMenuHandler}
        MenuListProps={{
          'aria-labelledby': 'basic-button'
        }}
      >
        {/* Edit option for admin comments */}
        {props.adminComment ? (
          <MenuItem onClick={editPostHandler}>{t('edit')}</MenuItem>
        ) : (
          <MenuItem onClick={reportPostHandler}>
            {t('report')}
          </MenuItem>
        )}
        {/* Delete option for academy admins */}
        {userInfo.data.academyAdmin && (
          <MenuItem onClick={deleteCommentHandler}>
            {t('eliminate')}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
CommentOptions.propTypes = {
  /** Indicates if the comment is by an admin */
  adminComment: PropTypes.bool,
  /** Lesson ID associated with the comment */
  idLesson: PropTypes.number,
  /** Course ID associated with the comment */
  idCourse: PropTypes.number,
  /** Indicates if the comment is a subcomment */
  subcomment: PropTypes.bool,
  /** ID of the comment or subcomment */
  idComment: PropTypes.number,
  /** Function to refresh answers after deletion */
  showAnswers: PropTypes.func,
  /** Function to trigger edit mode */
  editCommentHandler: PropTypes.func
};

CommentOptions.defaultProps = {
  adminComment: undefined,
  idLesson: undefined,
  idCourse: undefined,
  subcomment: undefined,
  idComment: undefined,
  showAnswers: undefined,
  editCommentHandler: undefined
};

export default CommentOptions;
